import { InfoType, MessageMention } from "../types";

type LegacyTagOption = {
  option?: string;
  loaded?: boolean;
};

type LegacyInfoTag = {
  type?: InfoType["type"];
  kind?: InfoType["type"];
  id?: unknown;
  label?: string;
  name?: string;
  state?: InfoType["state"];
  extraInfo?: InfoType["extraInfo"];
  account?: {
    accountId?: string;
    accountName?: string;
    accountKey?: string;
  } | null;
  options?: unknown;
  value?: string;
  key?: string | string[];
};

const toStringValue = (value: unknown) =>
  value === undefined || value === null ? "" : String(value);

const normalizeInfoTagId = (id: unknown): string | string[] =>
  Array.isArray(id) ? id.map(toStringValue).filter(Boolean) : toStringValue(id);

const getLoadedOptionList = (options: unknown): string[] => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .flatMap((item) => (Array.isArray(item) ? item : [item]))
    .map((option) => {
      if (typeof option === "string") {
        return option;
      }

      if (!option || typeof option !== "object") {
        return "";
      }

      const tagOption = option as LegacyTagOption;
      return tagOption.loaded === false ? "" : tagOption.option || "";
    })
    .filter(Boolean);
};

const getLoadedLegacyOptionList = (options: unknown): string[] => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .flatMap((item) => (Array.isArray(item) ? item : [item]))
    .filter((option): option is LegacyTagOption => !!option && typeof option === "object")
    .filter((option) => option.loaded && option.option)
    .map((option) => option.option as string);
};

const normalizeExtraInfo = (
  extraInfo: InfoType["extraInfo"] | undefined,
  options: unknown,
  useLegacyOptionLoaded = false
) => {
  const selectedList = useLegacyOptionLoaded
    ? getLoadedLegacyOptionList(options)
    : getLoadedOptionList(options);
  const nextExtraInfo = {
    ...(extraInfo ?? {}),
    ...(!extraInfo?.selectedList && selectedList.length ? { selectedList } : {}),
  };

  return Object.keys(nextExtraInfo).length > 0 ? nextExtraInfo : undefined;
};

export const getInfoTagSelectedList = (tag: InfoType | null | undefined): string[] =>
  Array.isArray(tag?.extraInfo?.selectedList) ? tag!.extraInfo!.selectedList!.filter(Boolean) : [];

export const normalizeInfoTag = (tag: LegacyInfoTag): InfoType => {
  if (tag?.type && tag.id !== undefined && tag.name !== undefined) {
    const extraInfo = normalizeExtraInfo(tag.extraInfo, tag.options);

    return {
      type: tag.type,
      id: normalizeInfoTagId(tag.id),
      name: toStringValue(tag.name),
      ...(tag.state ? { state: tag.state } : {}),
      ...(extraInfo ? { extraInfo } : {}),
    };
  }

  if (tag?.kind && tag.id !== undefined) {
    const type = tag.kind === "account" || tag.kind === "activity" ? tag.kind : "other";
    const extraInfo = normalizeExtraInfo(tag.extraInfo, tag.options);

    return {
      type,
      id: normalizeInfoTagId(tag.id),
      name: toStringValue(tag.label || tag.name || tag.id),
      ...(tag.state ? { state: tag.state } : {}),
      ...(extraInfo ? { extraInfo } : {}),
    };
  }

  if (tag?.account) {
    const extraInfo = normalizeExtraInfo(tag.extraInfo, tag.options, true);

    return {
      type: "account",
      id: toStringValue(tag.account.accountId || tag.account.accountKey),
      name: toStringValue(tag.account.accountName),
      ...(tag.state ? { state: tag.state } : {}),
      ...(extraInfo ? { extraInfo } : {}),
    };
  }

  return {
    type: "account",
    id: normalizeInfoTagId(tag?.key),
    name: toStringValue(tag?.value || tag?.key),
    ...(tag?.state ? { state: tag.state } : {}),
  };
};

export const normalizeInfoTags = (tags: unknown): InfoType[] =>
  Array.isArray(tags)
    ? tags.filter(Boolean).map((tag) => normalizeInfoTag(tag as LegacyInfoTag))
    : [];

export const infoTagsToMentions = (tags: unknown): MessageMention[] =>
  normalizeInfoTags(tags)
    .filter((tag) => tag.type === "account" || tag.type === "activity")
    .flatMap((tag) => {
      const ids = Array.isArray(tag.id) ? tag.id : [tag.id];
      const options = getInfoTagSelectedList(tag).map((option) => ({
        option,
        loaded: tag.state !== "loading",
      }));

      return ids.filter(Boolean).map((id) => ({
        kind: tag.type as "account" | "activity",
        id,
        label: tag.name || id,
        ...(options.length > 0 ? { options } : {}),
      }));
    });

export const getTopicInfoTags = (
  topic: { accountTags?: unknown; mentions?: unknown } | null | undefined
): InfoType[] => {
  const mentions = normalizeInfoTags(topic?.mentions);

  if (mentions.length > 0) {
    return mentions;
  }

  return normalizeInfoTags(topic?.accountTags);
};

export const getMessageInfoTags = (
  message: { accountTags?: unknown } | null | undefined
): InfoType[] => {
  return normalizeInfoTags(message?.accountTags);
};

export const normalizeMessageInfoTags = <T extends { accountTags?: unknown; mentions?: unknown }>(
  message: T
): Omit<T, "accountTags" | "mentions"> & { accountTags?: InfoType[] } => {
  const accountTags = getMessageInfoTags(message);
  const { accountTags: _accountTags, mentions: _mentions, ...restMessage } = message;

  return accountTags.length > 0 ? { ...restMessage, accountTags } : restMessage;
};

export const isInfoTagLoading = (tag: InfoType | null | undefined) => tag?.state === "loading";

export const isStringIdAccountTag = (
  tag: InfoType | null | undefined
): tag is InfoType & { type: "account"; id: string } =>
  tag?.type === "account" && typeof tag.id === "string";

export const getInfoTagIdLabel = (tag: InfoType | null | undefined) => {
  if (!tag) {
    return "";
  }

  if (Array.isArray(tag.id)) {
    return `${tag.id.length} accountIds`;
  }

  return tag.id;
};

export const areSelectedListsEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) {
    return false;
  }

  const next = new Set(a);
  return b.every((item) => next.has(item));
};
